import type { BaseCalculee, DeclarationMoisPaie, PaiePersonnel } from '../types';

/**
 * Les regles de lecture d'un calcul de paie, partagees par l'ecran Paie
 * et la fiche du salarie (2026-10-03) : heures decimales en heures et
 * minutes, partage des regimes, mois portes. Pas de composant ici — le
 * rechargement a chaud de Vite veut des fichiers qui n'exportent que des
 * composants, ou que des fonctions.
 */

/**
 * Heures decimales → heures et minutes (11,25 devient 11h 15min). La
 * conversion en minutes n'a sa place qu'a l'AFFICHAGE : le moteur reste
 * en heures decimales (P3). Le signe est porte devant — une base
 * negative (correction) donne « -2h 30min », jamais « -2h -30min ».
 * Les minutes gardent deux chiffres : « 11h 05min » s'aligne sous
 * « 11h 35min », pas « 11h 5min ».
 */
export function heuresMinutes(valeur: number): { signe: string; h: number; mm: string } {
  const minutes = Math.round(Math.abs(valeur) * 60);
  return {
    signe: valeur < 0 ? '-' : '',
    h: Math.floor(minutes / 60),
    mm: String(minutes % 60).padStart(2, '0'),
  };
}

/** La forme texte, pour l'export Excel. */
export function heuresMinutesTexte(valeur: number): string {
  const { signe, h, mm } = heuresMinutes(valeur);
  return `${signe}${h}h ${mm}min`;
}

export type RegimePaie = 'general' | 'cachet';

/**
 * Meme regle que le moteur : seul un creneau dont l'activite est
 * « intermittent » part au cachet ; tout le reste — permanent, non
 * renseigne, element exceptionnel sans creneau — releve du general.
 */
export const auCachet = (base: BaseCalculee) => base.cachet || base.creneau_activite_mode === 'intermittent';

/** Les elements d'un regime, et les mois qu'ils portent, tries. */
export function basesDuRegime(personnel: PaiePersonnel, mode: RegimePaie): BaseCalculee[] {
  return personnel.bases.filter((b) => auCachet(b) === (mode === 'cachet'));
}

/**
 * La declaration FIGEE d'un mois clos, ou null — un mois ouvert, ou clos
 * avant la 0.70.0 sans reprise, se lit sur ses lignes.
 */
export function declarationFigee(personnel: PaiePersonnel, mois: string): DeclarationMoisPaie | null {
  return personnel.declarations_mensuelles.find((d) => d.mois === mois && d.figee) ?? null;
}

/** La declaration porte-t-elle quelque chose dans ce regime ? */
export const declarationPorte = (d: DeclarationMoisPaie, mode: RegimePaie): boolean =>
  mode === 'cachet'
    ? d.cachet !== 0 || d.heures_cachet !== 0
    : d.salaire !== 0 || d.prime !== 0 || d.transport !== 0 || d.mutuelle !== 0 || d.heures !== 0;

/**
 * Les mois portes par ce regime : ceux de ses lignes, et les mois clos
 * dont la declaration figee porte quelque chose (0.70.0 — un mois clos
 * n'a plus de ligne de cours ni de salaire, sa declaration les resume).
 */
export function moisPortes(personnel: PaiePersonnel, mode: RegimePaie): string[] {
  const declares = personnel.declarations_mensuelles
    .filter((d) => d.figee && declarationPorte(d, mode))
    .map((d) => d.mois);
  return [...new Set([...basesDuRegime(personnel, mode).map((b) => b.mois), ...declares])].sort();
}
